import React, { useEffect, useRef, useImperativeHandle, forwardRef, memo } from "react";

const GoogleRecaptcha = memo(
  forwardRef(
    (
      {
        siteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY || "6Lcw_fwtAAAAAFRX_vqK2Gum6pw5kC9fYMoSSAP0",
        action = "LOGIN",
        onChange,
        onExpired,
        onError,
      },
      ref
    ) => {
      const containerRef = useRef(null);
      const widgetIdRef = useRef(null);

      // Keep latest callback references without triggering re-initialization of the effect
      const onChangeRef = useRef(onChange);
      const onExpiredRef = useRef(onExpired);
      const onErrorRef = useRef(onError);

      useEffect(() => {
        onChangeRef.current = onChange;
      }, [onChange]);

      useEffect(() => {
        onExpiredRef.current = onExpired;
      }, [onExpired]);

      useEffect(() => {
        onErrorRef.current = onError;
      }, [onError]);

      useImperativeHandle(ref, () => ({
        reset: () => {
          const grecaptcha = window.grecaptcha?.enterprise || window.grecaptcha;
          if (widgetIdRef.current !== null && grecaptcha && typeof grecaptcha.reset === "function") {
            try {
              grecaptcha.reset(widgetIdRef.current);
            } catch (e) {
              console.error("Error resetting reCAPTCHA:", e);
            }
          }
        },
        getResponse: () => {
          const grecaptcha = window.grecaptcha?.enterprise || window.grecaptcha;
          if (widgetIdRef.current !== null && grecaptcha && typeof grecaptcha.getResponse === "function") {
            try {
              return grecaptcha.getResponse(widgetIdRef.current);
            } catch (e) {
              return "";
            }
          }
          return "";
        },
      }));

      useEffect(() => {
        let isMounted = true;
        let intervalId = null;

        const getGrecaptcha = () => {
          return window.grecaptcha?.enterprise || window.grecaptcha;
        };

        const renderWidget = () => {
          if (!isMounted || !containerRef.current || widgetIdRef.current !== null) {
            return;
          }

          const grecaptcha = getGrecaptcha();
          if (grecaptcha && typeof grecaptcha.render === "function") {
            try {
              containerRef.current.innerHTML = "";
              const id = grecaptcha.render(containerRef.current, {
                sitekey: siteKey,
                action: action,
                callback: (token) => {
                  if (isMounted && onChangeRef.current) {
                    onChangeRef.current(token);
                  }
                },
                "expired-callback": () => {
                  if (isMounted && onExpiredRef.current) {
                    onExpiredRef.current();
                  }
                },
                "error-callback": (err) => {
                  if (isMounted && onErrorRef.current) {
                    onErrorRef.current(err);
                  }
                },
              });
              widgetIdRef.current = id;
              if (intervalId) {
                clearInterval(intervalId);
                intervalId = null;
              }
            } catch (err) {
              console.error("reCAPTCHA rendering error:", err);
            }
          }
        };

        const initRecaptcha = () => {
          const grecaptcha = getGrecaptcha();
          if (grecaptcha && typeof grecaptcha.render === "function") {
            if (typeof grecaptcha.ready === "function") {
              grecaptcha.ready(renderWidget);
            } else {
              renderWidget();
            }
          } else {
            intervalId = setInterval(() => {
              const g = getGrecaptcha();
              if (g && typeof g.render === "function") {
                if (intervalId) {
                  clearInterval(intervalId);
                  intervalId = null;
                }
                if (typeof g.ready === "function") {
                  g.ready(renderWidget);
                } else {
                  renderWidget();
                }
              }
            }, 200);
          }
        };

        const scriptId = "google-recaptcha-script";
        let script = document.getElementById(scriptId);
        if (!script) {
          script = document.createElement("script");
          script.id = scriptId;
          script.src = "https://www.google.com/recaptcha/enterprise.js?render=explicit";
          script.async = true;
          script.defer = true;
          script.onload = () => {
            initRecaptcha();
          };
          document.head.appendChild(script);
        } else {
          initRecaptcha();
        }

        return () => {
          isMounted = false;
          if (intervalId) {
            clearInterval(intervalId);
            intervalId = null;
          }
          const grecaptcha = getGrecaptcha();
          if (widgetIdRef.current !== null && grecaptcha && typeof grecaptcha.reset === "function") {
            try {
              grecaptcha.reset(widgetIdRef.current);
            } catch (e) {
              // ignore cleanup reset error
            }
          }
          widgetIdRef.current = null;
        };
      }, [siteKey, action]);

      return (
        <div
          className="recaptcha-wrapper"
          style={{
            display: "flex",
            justifyContent: "center",
            margin: "12px 0",
            minHeight: "78px",
          }}
        >
          <div ref={containerRef} />
        </div>
      );
    }
  )
);

export default GoogleRecaptcha;
